/* eslint-disable max-len */
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { QUERY_KEY_FOR_STEPPER } from './CepForm.constants';
import { cepFormStepIndexer } from './CepForm.states';
import { mayaCepListRoute, mayaCepTemplateListRoute } from 'basics/constants/routes.constants';
import { CepStatus, CepSteps, VeevaSyncStatus } from 'basics/enums/maya.enums';
import { useRemoveTargetsByMdmIdListMutation } from 'basics/graphql/mutations/removeTargetsByMdmIdList';
import { useSetCepStatusToApprovedMutation } from 'basics/graphql/mutations/setCepStatusToApproved';
import { useSetCepStatusToFeedbackMutation } from 'basics/graphql/mutations/setCepStatusToFeedback';
import { useSyncTacticGroupsToVeevaMutation } from 'basics/graphql/mutations/syncTacticGroupsToVeeva';
import { useSyncTargetsToVeevaMutation } from 'basics/graphql/mutations/syncTargetsToVeeva';
import { useUpdateCustomerInitiativeMutation } from 'basics/graphql/mutations/updateCustomerInitiative';
import useGetCepByIdLazyQuery from 'basics/graphql/queries/getCepByIdLazy';
import useGetTargetListByCepIdLazyQuery from 'basics/graphql/queries/getTargetListByCepIdLazy';
import { cepToBE } from 'basics/transformers/Cep.transformer';
import { CepConfigurationStepStateType, CepStateType } from 'basics/types/maya.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import { CepFormStepsIndex, CepFormSubmitCallbackType } from 'components/CepForm/CepForm.types';
import { useTacticFieldsValidation } from 'components/TacticFieldsValidationDialog';
import useTacticValidation from 'components/TacticValidationDialog/TacticValidation.hook';
import { CustomerEngagementPlanning, Target } from 'generated/maya.types';
import { useQueryParams } from 'hooks/useQueryParams';
import useStepIndexer from 'hooks/useStepIndexer';
import cepState from 'states/cep.states';
import cepConfigurationStepState from 'states/cepConfigurationStep.states';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useCepFormActions = (yodaFormMethods: any, submitCallback: CepFormSubmitCallbackType, isTemplate: boolean) => {
  const { t } = useTranslation();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
  const stepIndexer = useStepIndexer(cepFormStepIndexer);
  const { stepIndex, moveNextStep, movePreviousStep, moveToStep } = stepIndexer;
  const { getValues, formState, setStepShowError } = yodaFormMethods;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { cepSourceId } = useQueryParams();
  const [setCepStatusToApproved] = useSetCepStatusToApprovedMutation();
  const [setCepStatusToFeedback] = useSetCepStatusToFeedbackMutation();
  const [syncTargetsToVeeva, targetsSyncState] = useSyncTargetsToVeevaMutation();
  const [updateCustomerInitiative] = useUpdateCustomerInitiativeMutation();
  const { queryGetCepById } = useGetCepByIdLazyQuery();
  const [removeTargetsByMdmIdList] = useRemoveTargetsByMdmIdListMutation();
  const { queryGetTargetListByCepId } = useGetTargetListByCepIdLazyQuery();
  const flags = useFlags();
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const cepConfigurationStepValue = useSnapshot(cepConfigurationStepState) as CepConfigurationStepStateType;
  const { notValidGroups } = useTacticValidation();
  const {
    invalidTactics,
    cepDateError,
    validationError,
    validateTacticsFields,
  } = useTacticFieldsValidation();
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [openFieldsValidationDialog, setOpenFieldsValidationDialog] = useState(false);
  const [syncTacticGroupsToVeeva] = useSyncTacticGroupsToVeevaMutation();

  useEffect(() => {
    const newParams = new URLSearchParams(window.location.search);

    if (newParams.has(QUERY_KEY_FOR_STEPPER)) {
      const step = newParams.get(QUERY_KEY_FOR_STEPPER);
      stepIndexer.moveToStep(Number(step));
      newParams.delete(QUERY_KEY_FOR_STEPPER);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cepConfigurationStepValue.isOpenConfirmationModal) {
      setOpenConfirmationModal(cepConfigurationStepValue.isOpenConfirmationModal);
    }
  }, [cepConfigurationStepValue.isOpenConfirmationModal]);

  const setShowError = useCallback(
    (stepIndexVal: number) => {
      const step = CepFormStepsIndex[stepIndexVal];
      setStepShowError(true, [step]);
    },
    [setStepShowError],
  );

  const handleSaveData = useCallback(async (isOnConfigurationStep = false) => {
    if (formState.isDirty) {
      setIsSubmitting(true);

      const customerEngagementPlanningInput = getValues();
      if (customerEngagementPlanningInput) {
        const formattedCEP = cepToBE(customerEngagementPlanningInput);
        const newCEPDateEnd = new Date(formattedCEP.dateEnd).toISOString();

        const currentCustomerInitiativeData = cepState?.cep?.customerInitiative;
        const newCustomerInitiativeData = {
          title: currentCustomerInitiativeData?.title,
          description: currentCustomerInitiativeData?.description,
          dateEnd: newCEPDateEnd,
        };

        if (
          !cepConfigurationStepState.isOpenConfirmationModal
          && isCepApproved
          && flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableEditingEndDateOfApprovedCep
          && newCEPDateEnd !== cepState?.cep?.dateEnd
          && isOnConfigurationStep
        ) {
          cepConfigurationStepState.isOpenConfirmationModal = true;
          setOpenConfirmationModal(true);
          setIsSubmitting(false);
          return null;
        }

        setOpenConfirmationModal(false);
        cepConfigurationStepState.isOpenConfirmationModal = false;

        if (
          flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableEditingEndDateOfApprovedCep
          && newCustomerInitiativeData.dateEnd > currentCustomerInitiativeData?.dateEnd
        ) {
          const { data: customerInitiativeData, error } = await updateCustomerInitiative(
            currentCustomerInitiativeData?._id || null,
            { ...newCustomerInitiativeData, isTemplate: false },
          );

          if (customerInitiativeData && !error) {
            succesToast(t('maya_ci_updated', { title: newCustomerInitiativeData.title }));
          } else {
            warningToast(error || t('maya_ci_updated_error', { title: newCustomerInitiativeData.title }));
          }
        }

        const customerEngagementPlanningData = await submitCallback(formattedCEP);

        const { data: cep, error } = customerEngagementPlanningData;

        cepState.cep = cep || {};
        setIsSubmitting(false);
        if (cep) {
          succesToast(t('toast_draft_saved'));

          return cep;
        }

        warningToast(error || t('errors_standard'));

        return null;
      }
    } else {
      formState.showError = true;
      setShowError(stepIndex);
    }

    return cepStateValue.cep;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepStateValue.cep, formState, getValues, setShowError, stepIndex, submitCallback, t]);

  const getTacticsValidationOptions = useCallback(async (
    cep?: Partial<CustomerEngagementPlanning> | null,
  ): Promise<{
    validateRequiredFields: boolean;
    validateOptionsAvailability: boolean;
  }> => {
    if (!cep?._id) {
      return {
        validateRequiredFields: false,
        validateOptionsAvailability: false,
      };
    }

    const currentCustomerInitiative = cep.customerInitiative || cepState.cep?.customerInitiative;

    if (currentCustomerInitiative?.isTemplate) {
      return {
        validateRequiredFields: true,
        validateOptionsAvailability: false,
      };
    }

    const sourceCepId = cepSourceId || cep.sourceId || cepState.cep?.sourceId;

    if (!sourceCepId) {
      return {
        validateRequiredFields: false,
        validateOptionsAvailability: false,
      };
    }

    const { data: sourceCepResponse } = await queryGetCepById({
      variables: { id: sourceCepId },
    });

    const sourceCep = sourceCepResponse?.getCustomerEngagementPlanningById;

    let templateCustomerInitiative = sourceCep?.customerInitiative?.isTemplate
      ? sourceCep.customerInitiative
      : null;

    if (!templateCustomerInitiative && sourceCep?.sourceId) {
      const { data: parentSourceCepResponse } = await queryGetCepById({
        variables: { id: sourceCep.sourceId },
      });

      const parentSourceCep = parentSourceCepResponse?.getCustomerEngagementPlanningById;

      if (parentSourceCep?.customerInitiative?.isTemplate) {
        templateCustomerInitiative = parentSourceCep.customerInitiative;
      }
    }

    const validateRequiredFields = Boolean(templateCustomerInitiative);
    const validateOptionsAvailability = Boolean(
      validateRequiredFields
        && templateCustomerInitiative?.country
        && currentCustomerInitiative?.country
        && templateCustomerInitiative.country !== currentCustomerInitiative.country,
    );

    return {
      validateRequiredFields,
      validateOptionsAvailability,
    };
  }, [cepSourceId, queryGetCepById]);

  const handleSyncTarget = useCallback(async () => {
    let targetsSynched = true;
    const isTargetsNotSynched = cepStateValue.targetList.filter((target) => target.veevaSyncStatus !== VeevaSyncStatus.SYNC);
    if (isTemplate) {
      return targetsSynched;
    }
    if (CepFormStepsIndex[stepIndex] === CepSteps.targetListStep && isTargetsNotSynched.length) {
      setIsSubmitting(true);
      const cepId = cepStateValue.cep?._id;

      if (cepId) {
        const { data: pendingDeletionData } = await queryGetTargetListByCepId({
          variables: {
            cepId, limit: 5000, offset: 0,
          },
        });

        const mdmIdsForDeletion: string[] = pendingDeletionData?.getTargetListByCepId?.targets?.filter((target: Target) => target.veevaSyncStatus === VeevaSyncStatus.PENDING_DELETION)
          .map((target: Target) => target.mdmId) || [];

        if (mdmIdsForDeletion.length > 0) {
          const {
            data: removeTargetByMdmIdListData,
            error: removeTargetByMdmIdListError,
          } = await removeTargetsByMdmIdList(mdmIdsForDeletion, cepId);

          if (removeTargetByMdmIdListData !== null && !removeTargetByMdmIdListError) {
            const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
            if (cepState.cep) {
              cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
            }
          }
        }

        const { data: syncTarget } = await syncTargetsToVeeva(cepId);
        targetsSynched = Boolean(syncTarget?.syncVeeva);

        if (syncTarget && Array.isArray(syncTarget?.errors)) {
          syncTarget.errors.forEach((error) => {
            warningToast(error);
          });
        }

        const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
        if (cepState.cep) {
          cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
        }
      }

      if (targetsSynched) {
        cepState.targetList = [];
      }

      setIsSubmitting(false);
    }

    return targetsSynched;
  }, [
    cepStateValue.cep?._id,
    cepStateValue.targetList,
    isTemplate,
    queryGetCepById,
    queryGetTargetListByCepId,
    removeTargetsByMdmIdList,
    stepIndex,
    syncTargetsToVeeva,
  ]);

  const handlePublishEvent = useCallback(async () => {
    if (formState.isValid) {
      const customerEngagementPlanInput = getValues();

      if (notValidGroups.length && flags.mayaBdcfcsd1585EnableTacticGroup) {
        setOpenValidationDialog(true);
        return;
      }

      if (customerEngagementPlanInput) {
        const cep = await handleSaveData();
        const cepId = cep?._id || cepState.cep?._id;

        if (cepId) {
          const tacticsValidationOptions = await getTacticsValidationOptions(cep);

          if (
            tacticsValidationOptions.validateRequiredFields
              || tacticsValidationOptions.validateOptionsAvailability
          ) {
            const tacticFieldsValid = await validateTacticsFields(cepId, tacticsValidationOptions);

            if (!tacticFieldsValid) {
              setOpenFieldsValidationDialog(true);
              return;
            }
          }
        }

        const cepStatus = cep?.status || cepState.cep?.status;

        setIsSubmitting(true);
        if (cepStatus && cepId && cepStatus === CepStatus.DRAFT) {
          const { data: cepFeedback, error: cepFeedbackError } = await setCepStatusToFeedback(cepId);
          if (cepFeedback && !cepFeedbackError) {
            succesToast(t('maya_action_feedback_cep_success'));
            navigate(mayaCepListRoute);
          } else {
            warningToast(cepFeedbackError || t('maya_action_feedback_cep_error'));
          }
        }

        if (cepStatus && cepId && cepStatus === CepStatus.FEEDBACK) {
          const { data, error } = await syncTacticGroupsToVeeva(cepId);

          if (data && !error) {
            succesToast(t('maya_action_approve_cep_group_success_sync'));
          } else if (error) {
            warningToast(error || t('maya_action_approve_cep_error'));
            setIsSubmitting(false);
            return;
          }

          const { data: cepApproved, error: cepApprovedError } = await setCepStatusToApproved(cepId);
          if (cepApproved && !cepApprovedError) {
            succesToast(t('maya_action_approve_cep_success'));
            navigate(mayaCepListRoute);
          } else {
            warningToast(cepApprovedError || t('maya_action_approve_cep_error'));
          }
        }

        setIsSubmitting(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState.isValid,
    flags.mayaBdcfcsd1585EnableTacticGroup,
    getValues,
    cepStateValue,
    handleSaveData,
    getTacticsValidationOptions,
    setCepStatusToFeedback,
    t,
    navigate,
    setCepStatusToApproved,
    validateTacticsFields,
  ]);

  const handleChangeStep = useCallback(
    (stepIndexVal: number) => {
      const previousStep = stepIndexer.previousStep || 0;
      let targetStep = stepIndexVal;
      if (!flags.mayaBiogenlinc4793EnableGoalStep && stepIndexVal >= CepFormStepsIndex.goalStep) {
        targetStep += 1;
      }
      stepIndexer.moveToStep(targetStep);
      setShowError(previousStep);
    },
    [flags.mayaBiogenlinc4793EnableGoalStep, setShowError, stepIndexer],
  );

  const enableDuplicateSave = useCallback(() => {
    if (stepIndex === CepFormStepsIndex.configurationStep && cepSourceId) {
      formState.isDirty = true;
    }
  }, [formState, stepIndex, cepSourceId]);

  const saveAndNext = useCallback(async (isOnConfigurationStep: boolean) => {
    enableDuplicateSave();
    const cep = await handleSaveData(isOnConfigurationStep);
    const targetsSynched = await handleSyncTarget();
    setShowError(stepIndex);

    if (cep && targetsSynched) {
      if (!flags.mayaBiogenlinc4793EnableGoalStep && stepIndex + 1 === CepFormStepsIndex.goalStep) {
        moveToStep(CepFormStepsIndex.goalStep + 1);
      } else {
        moveNextStep();
      }
    }
  }, [
    enableDuplicateSave,
    flags.mayaBiogenlinc4793EnableGoalStep,
    handleSaveData,
    handleSyncTarget,
    moveNextStep,
    moveToStep,
    setShowError,
    stepIndex,
  ]);

  const saveAndPrevious = useCallback(() => {
    handleSaveData();
    setShowError(stepIndex);

    if (!flags.mayaBiogenlinc4793EnableGoalStep && stepIndex - 1 === CepFormStepsIndex.goalStep) {
      moveToStep(CepFormStepsIndex.goalStep - 1);
    } else {
      movePreviousStep();
    }
  }, [flags.mayaBiogenlinc4793EnableGoalStep, handleSaveData, movePreviousStep, moveToStep, setShowError, stepIndex]);

  const saveAsDraft = useCallback(() => {
    handleSaveData();
    setShowError(stepIndex);
    if (isTemplate) {
      navigate(mayaCepTemplateListRoute);
    } else {
      navigate(mayaCepListRoute);
    }
  }, [handleSaveData, isTemplate, navigate, setShowError, stepIndex]);

  const handleProceed = () => {
    saveAndNext(true);
  };

  const handleCancel = () => {
    setOpenConfirmationModal(false);
    cepConfigurationStepState.isOpenConfirmationModal = false;
  };

  return {
    handleChangeStep,
    handlePublishEvent,
    handleSaveData,
    saveAsDraft,
    setShowError,
    stepIndexer,
    saveAndNext,
    saveAndPrevious,
    syncTargetsLoading: targetsSyncState.loading,
    isSubmitting,
    openConfirmationModal,
    handleProceed,
    handleCancel,
    openValidationDialog,
    setOpenValidationDialog,
    openFieldsValidationDialog,
    setOpenFieldsValidationDialog,
    invalidTactics,
    cepDateError,
    validationError,
  };
};

export default useCepFormActions;
